import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { CHECK_ASSIGNMENT_AVAILABILITY } from '../../graphql/team-availability-queries';

const REASON_LABELS = {
  HOLIDAY: 'Company holiday',
  VACATION: 'Time off',
  OFF: 'Not scheduled',
};

const REASON_BADGE_CLASSES = {
  HOLIDAY: 'bg-red-100 text-red-800 border-red-200',
  VACATION: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  OFF: 'bg-gray-100 text-gray-800 border-gray-200',
};

const REASON_PRIORITY = {
  HOLIDAY: 0,
  VACATION: 1,
  OFF: 2,
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDay = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;
};

const buildDayInputs = (dates) => {
  if (!Array.isArray(dates)) return [];
  const seen = new Set();
  const output = [];
  dates.forEach((value) => {
    const key = toIsoDay(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    output.push(`${key}T12:00:00.000Z`);
  });
  return output;
};

const dedupeIntIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  const output = [];
  ids.forEach((raw) => {
    if (raw === null || raw === undefined || raw === '') return;
    const asNumber = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(asNumber) || asNumber <= 0) return;
    if (seen.has(asNumber)) return;
    seen.add(asNumber);
    output.push(asNumber);
  });
  return output;
};

/**
 * Renders a warning banner when any of the given users are unavailable on any
 * of the given dates. Renders nothing if there are no conflicts (or if inputs
 * are empty).
 *
 * Consumers pass:
 *   - userIds: array of user ids (numbers or numeric strings) that would be assigned
 *   - dates: array of Date objects or ISO/YYYY-MM-DD strings for the days in question
 *   - variant: 'compact' (inline near a select) or 'full' (multi-day summary)
 *   - title: optional heading text
 *   - subtitle: optional supporting sentence
 */
function AssignmentAvailabilityWarning({
  userIds,
  dates,
  variant = 'compact',
  title,
  subtitle,
  className = '',
}) {
  const normalizedUserIds = useMemo(() => dedupeIntIds(userIds), [userIds]);
  const normalizedDates = useMemo(() => buildDayInputs(dates), [dates]);

  const skip = normalizedUserIds.length === 0 || normalizedDates.length === 0;

  const { data } = useQuery(CHECK_ASSIGNMENT_AVAILABILITY, {
    variables: { userIds: normalizedUserIds, dates: normalizedDates },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const conflicts = useMemo(() => data?.checkAssignmentAvailability || [], [data]);

  const sortedConflicts = useMemo(() => {
    return [...conflicts].sort((a, b) => {
      const dateA = parseDate(a.date)?.getTime() || 0;
      const dateB = parseDate(b.date)?.getTime() || 0;
      if (dateA !== dateB) return dateA - dateB;
      const priorityDiff = (REASON_PRIORITY[a.reason] ?? 99) - (REASON_PRIORITY[b.reason] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;
      return (a.userName || '').localeCompare(b.userName || '');
    });
  }, [conflicts]);

  if (skip || sortedConflicts.length === 0) return null;

  const headingText =
    title ||
    (variant === 'compact'
      ? "Heads up — assignee may not be available"
      : 'Availability warning');
  const supportingText =
    subtitle ||
    (variant === 'compact'
      ? 'You can still assign, but the team member is flagged as unavailable on this date.'
      : 'Assignments can still proceed, but some team members are flagged as unavailable on the selected days.');

  return (
    <div
      role="status"
      aria-live="polite"
      className={`mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 ${className}`}
    >
      <div className="flex items-start gap-2">
        <svg
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{headingText}</p>
          <p className="text-xs text-amber-800 mt-0.5">{supportingText}</p>
          <ul className="mt-2 space-y-1">
            {sortedConflicts.map((conflict, index) => {
              const dateObj = parseDate(conflict.date);
              const dateLabel = dateObj
                ? format(dateObj, 'EEE, MMM d, yyyy')
                : String(conflict.date);
              const reasonLabel = REASON_LABELS[conflict.reason] || conflict.reason || 'Off';
              const badgeClass =
                REASON_BADGE_CLASSES[conflict.reason] ||
                'bg-gray-100 text-gray-800 border-gray-200';
              return (
                <li
                  key={`${conflict.userId}-${dateLabel}-${index}`}
                  className="text-xs flex flex-wrap items-center gap-x-2 gap-y-1"
                >
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${badgeClass}`}
                  >
                    {reasonLabel}
                  </span>
                  <span className="font-medium text-amber-900">{conflict.userName}</span>
                  <span className="text-amber-800">— {dateLabel}</span>
                  {conflict.detail && (
                    <span className="text-amber-700">· {conflict.detail}</span>
                  )}
                  {conflict.notes && (
                    <span className="text-amber-700 italic">"{conflict.notes}"</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AssignmentAvailabilityWarning;
